// redux/contacts/contactThunk.js
import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../lib/axios';
import { toast } from 'react-toastify';

// Fetch general contacts
export const fetchContacts = createAsyncThunk(
  'contacts/fetch',
  async (projectId, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (projectId) params.append('projectId', projectId);
      const url = `/api/contacts${projectId ? `?${params.toString()}` : ''}`;
      const res = await api.get(url);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Add a new contact
export const addContact = createAsyncThunk(
  'contacts/add',
  async (contact, { rejectWithValue }) => {
    try {
      const res = await api.post('/api/addContacts', contact);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Update a contact
export const updateContact = createAsyncThunk(
  'contacts/update',
  async ({ id, updatedData }, { rejectWithValue }) => {
    try {
      const res = await api.put(`/api/updateContact/${id}`, updatedData);
      toast.success('Contact updated');
      return res.data;
    } catch (err) {
      toast.error('Failed to update');
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Delete a contact
export const deleteContact = createAsyncThunk(
  'contacts/delete',
  async ({ id, projectId }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (projectId) params.append('projectId', projectId);
      const url = `/api/deleteContact/${id}${projectId ? `?${params.toString()}` : ''}`;
      await api.delete(url);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Add tags to a contact
export const addTags = createAsyncThunk(
  'contacts/tags/add',
  async ({ id, tags, projectId }, { rejectWithValue }) => {
    try {
      const res = await api.patch(`/api/contacts/${id}/addTags`, { tags, projectId });
      return res.data;
    } catch (err) {
      toast.error('Failed to update tags');
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Remove a tag from a contact
export const removeTags = createAsyncThunk(
  'contacts/tags/remove',
  async ({ id, tag, projectId }, { rejectWithValue }) => {
    try {
      const res = await api.patch(`/api/contacts/${id}/tags/remove`, { tag, projectId });
      return res.data;
    } catch (err) {
      toast.error('Failed to remove tag');
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Add notes to a contact
export const addNotes = createAsyncThunk(
  'contacts/notes/add',
  async ({ id, notes, projectId }, { rejectWithValue }) => {
    try {
      const res = await api.patch(`/api/contacts/${id}/addNotes`, { notes, projectId });
      return res.data;
    } catch (err) {
      toast.error('Failed to add note');
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Remove a note from a contact
export const removeNotes = createAsyncThunk(
  'contacts/notes/remove',
  async ({ id, noteId, projectId }, { rejectWithValue }) => {
    try {
      const res = await api.patch(`/api/contacts/${id}/notes/remove`, { noteId, projectId });
      return res.data;
    } catch (err) {
      toast.error('Failed to remove note');
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Update a note for a contact
export const updateNote = createAsyncThunk(
  'contacts/notes/update',
  async ({ id, noteId, updatedText, projectId }, { rejectWithValue }) => {
    try {
      const res = await api.patch(`/api/contacts/${id}/notes/update`, { noteId, updatedText, projectId });
      return res.data;
    } catch (err) {
      toast.error('Failed to update note');
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Bulk upsert contacts
export const upsertContacts = createAsyncThunk(
  'contacts/upsert',
  async ({ contacts, projectId }, { rejectWithValue }) => {
    try {
      const res = await api.post('/api/contacts/bulk', { contacts, projectId });
      return res.data;
    } catch (err) {
      toast.error('Failed to upsert contacts');
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// FETCH CHAT LIST - Use /api/chat endpoint
export const fetchChatList = createAsyncThunk(
  'chatList/fetch',
  async (projectId, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (projectId) params.append('projectId', projectId);
      const url = `/api/chat${projectId ? `?${params.toString()}` : ''}`;
      const res = await api.get(url);
      return res.data;
    } catch (err) {
      toast.error('Failed to get Chat List');
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);
// Assign contact to multiple agents
export const assignContact = createAsyncThunk(
  'contacts/assign',
  async ({ id, assignedToAgents }, { rejectWithValue }) => {
    try {
      const res = await api.patch(`/api/contacts/${id}/assign`, { assignedToAgents });
      toast.success(res.data.message);
      return res.data;
    } catch (err) {
      toast.error('Failed to assign chat');
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);
